import React, { Fragment } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Form, { Field } from '@atlaskit/form';
import ArrowUpIcon from '@atlaskit/icon/core/arrow-up';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import TextArea from '@atlaskit/textarea';
import { token } from '@atlaskit/tokens';

import GradientRovoIcon from '../../common/assets/GradientRovoIcon';
import { type GenerateImageProps } from '../../types';

const i18n = defineMessages({
	getCreativeLabel: {
		id: 'ai-image-generation.rovo-header-image-generator.get-creative-label',
		defaultMessage: 'Get creative with Rovo',
		description: 'Label text for the Rovo header generation entry point',
	},
	getCreativePlaceholder: {
		id: 'ai-image-generation.rovo-header-image-generator.get-creative-placeholder',
		defaultMessage: 'Describe an image to create',
		description: 'Placeholder text for the Rovo header generation entry point',
	},
	rovoIconAlt: {
		id: 'ai-image-generation.rovo-header-image-generator.rovo-icon-alt',
		defaultMessage: 'Rovo icon',
		description: 'Alt text for the Rovo icon',
	},
	submitButtonLabel: {
		id: 'ai-image-generation.rovo-header-image-generator.submit-button-label',
		defaultMessage: 'Generate AI header image',
		description: 'Label for the Rovo header generation submit button',
	},
});

interface ImageGenerationFieldData {
	promptInput: string;
}

type RovoHeaderImageGeneratorProps = {
	deletePreviousHeaderImage?: () => void;
	generateAIHeaderImage?: (props: GenerateImageProps) => Promise<void>;
};

const styles = cssMap({
	divider: {
		width: '100%',
		height: '1px',
		marginBlock: token('space.075'),
		backgroundColor: token('color.background.accent.gray.subtler'),
	},
	rovoIcon: {
		width: '16px',
		height: '16px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	imagePromptContainer: {
		width: '100%',
		display: 'flex',
		alignItems: 'center',
		alignSelf: 'stretch',
		justifyContent: 'center',
		borderStyle: 'solid',
		borderRadius: token('radius.large'),
		borderColor: token('color.border'),
		borderWidth: token('border.width'),
		backgroundColor: token('elevation.surface'),
		paddingInline: token('space.150'),
		paddingBlockEnd: token('space.100'),
		marginBlockStart: token('space.150'),
	},
	stack: {
		width: '100%',
		flexDirection: 'column',
	},
});

export const RovoHeaderImageGenerator = ({
	generateAIHeaderImage,
	deletePreviousHeaderImage,
}: RovoHeaderImageGeneratorProps): JSX.Element | null => {
	const { formatMessage } = useIntl();

	/* IMPORTANT: The FG `confluence_aifc_image_generation` must always be gated behind `aifc_create_enabled`
	on Statsig. Otherwise, this component will be shown outside of the AIFC release */
	if (!generateAIHeaderImage || !fg('confluence_aifc_image_generation')) {
		return null;
	}

	return (
		<>
			<Box xcss={styles.divider} />

			<Form
				onSubmit={(data: ImageGenerationFieldData) => {
					deletePreviousHeaderImage?.();
					generateAIHeaderImage({ adfContent: data.promptInput });
				}}
			>
				{({ formProps }) => (
					<form {...formProps}>
						<Inline space="space.075" alignBlock="center">
							<Box xcss={styles.rovoIcon}>
								<GradientRovoIcon label={formatMessage(i18n.rovoIconAlt)} />
							</Box>
							<label htmlFor="get-creative-text-area">
								<Text size="medium" id="get-creative-label">
									{formatMessage(i18n.getCreativeLabel)}
								</Text>
							</label>
						</Inline>

						<Box xcss={styles.imagePromptContainer}>
							{/* Not using a Stack component directly as it prevents the TextArea from expanding */}
							<Box xcss={styles.stack}>
								<Field isRequired name="promptInput" id="get-creative-text-area">
									{({ fieldProps }: any) => (
										<Fragment>
											<TextArea
												placeholder={formatMessage(i18n.getCreativePlaceholder)}
												appearance="none"
												{...fieldProps}
											/>
										</Fragment>
									)}
								</Field>
								<Inline space="space.075" alignInline="end">
									<IconButton
										appearance="primary"
										icon={ArrowUpIcon}
										label={formatMessage(i18n.submitButtonLabel)}
										shape="circle"
										type="submit"
									/>
								</Inline>
							</Box>
						</Box>
					</form>
				)}
			</Form>
		</>
	);
};
